/**
 * The brand website a visitor types into the home page hero, carried across
 * sign-up so the onboarding brand step opens with it filled in.
 *
 * Stored in `localStorage` (not `sessionStorage`) because Clerk's email
 * verification can finish the sign-up in a different tab. The value expires
 * after a day and is cleared when the brand step submits. Pure
 * parse/serialize helpers are exported for tests; the storage accessors
 * swallow storage errors (private mode, quota).
 */

const STORAGE_KEY = 'brew:landing-brand-domain'

/**
 * The hero form's field name, and so the `/sign-up` query parameter a plain
 * (pre-hydration or no-JS) form submit carries the website in.
 */
export const LANDING_BRAND_DOMAIN_PARAM = 'website'

/** `/sign-up?website=acme.com` — where a valid hero submit goes. */
export function landingBrandSignUpHref(signUpPath: string, domain: string) {
  return `${signUpPath}?${new URLSearchParams({ [LANDING_BRAND_DOMAIN_PARAM]: domain })}`
}

export const LANDING_BRAND_DOMAIN_MAX_AGE_MS = 24 * 60 * 60 * 1000

/** Host labels joined by dots, with a letter-only TLD of two or more. */
const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/

/**
 * Reduce what the visitor typed to a bare lowercase host, or `null` when it
 * is not a domain (`https://www.acme.com/pricing` → `acme.com`).
 */
export function toLandingBrandDomain(input: string): string | null {
  const host = input
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .split(/[/?#]/)[0]
    .replace(/^www\./, '')

  return DOMAIN_PATTERN.test(host) ? host : null
}

export function serializeLandingBrandDomain(
  domain: string,
  now: number
): string {
  return JSON.stringify({ domain, savedAt: now })
}

export function parseStoredLandingBrandDomain(
  raw: string | null,
  now: number
): string | null {
  if (!raw) {
    return null
  }
  try {
    const value: unknown = JSON.parse(raw)
    if (
      typeof value !== 'object' ||
      value === null ||
      !('domain' in value) ||
      !('savedAt' in value) ||
      typeof value.domain !== 'string' ||
      typeof value.savedAt !== 'number'
    ) {
      return null
    }
    if (now - value.savedAt > LANDING_BRAND_DOMAIN_MAX_AGE_MS) {
      return null
    }
    return toLandingBrandDomain(value.domain)
  } catch {
    return null
  }
}

export function saveLandingBrandDomain(domain: string): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      serializeLandingBrandDomain(domain, Date.now())
    )
  } catch {
    // Storage unavailable: the brand step simply opens empty.
  }
}

/** The saved domain, or `null` when absent, expired, or unreadable. */
export function readLandingBrandDomain(): string | null {
  try {
    return parseStoredLandingBrandDomain(
      window.localStorage.getItem(STORAGE_KEY),
      Date.now()
    )
  } catch {
    return null
  }
}

/** Forget the saved domain once the brand step has used it. */
export function clearLandingBrandDomain(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear.
  }
}
